import { windowManager } from './systems/WindowManager.js';

// Make panels draggable and save position
export function makeDraggable(element, options = {}) {
  // Register with window manager if it's a managed window
  if (options.managed !== false) {
    windowManager.register(element, options);
  }
  
  // Extract callback from options
  const { onDragEnd } = options;
  
  let isDragging = false;
  let currentX;
  let currentY;
  let initialX;
  let initialY;
  let xOffset = 0;
  let yOffset = 0;
  
  // Get initial transform if any
  const computedStyle = window.getComputedStyle(element);
  const transform = computedStyle.transform;
  
  if (transform && transform !== 'none') {
    const matrix = new DOMMatrix(transform);
    xOffset = matrix.m41 || 0;
    yOffset = matrix.m42 || 0;
  }
  
  // Find draggable handle (title bar)
  // First try to find any element with a class ending in '-title'
  const titleBar = element.querySelector('[class*="-title"]') || 
                   element.querySelector('.log-window-title, #statsTitleBar, .config-window-title');
  const dragHandle = titleBar || element;
  
  // Add cursor style to drag handle
  dragHandle.style.cursor = 'move';
  
  function dragStart(e) {
    // Check if the drag started on a scrollable area
    const target = e.target;
    const isScrollable = target.closest('[style*="overflow-y: auto"]') || 
                        target.closest('[style*="overflow: auto"]');
    
    // Don't start drag if clicking on scrollable content
    if (isScrollable && isScrollable !== element) {
      return;
    }
    
    // Only allow dragging from the title bar or drag handle
    const clickedOnHandle = dragHandle.contains(target);
    if (!clickedOnHandle) {
      return;
    }
    
    if (e.type === "touchstart") {
      initialX = e.touches[0].clientX - xOffset;
      initialY = e.touches[0].clientY - yOffset;
    } else {
      initialX = e.clientX - xOffset;
      initialY = e.clientY - yOffset;
    }

    isDragging = true;
  }

  function dragEnd(e) {
    initialX = currentX;
    initialY = currentY;

    isDragging = false;
    
    // Call the onDragEnd callback if provided
    if (onDragEnd) {
      onDragEnd(xOffset, yOffset);
    }
  }

  function drag(e) {
    if (isDragging) {
      e.preventDefault();
      
      if (e.type === "touchmove") {
        currentX = e.touches[0].clientX - initialX;
        currentY = e.touches[0].clientY - initialY;
      } else {
        currentX = e.clientX - initialX;
        currentY = e.clientY - initialY;
      }

      // Get element dimensions
      const rect = element.getBoundingClientRect();
      const elementWidth = rect.width;
      const elementHeight = rect.height;
      
      // Get viewport dimensions (canvas fills the viewport)
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Get the base position (without transform)
      const computedStyle = window.getComputedStyle(element);
      const baseLeft = parseFloat(computedStyle.left) || 0;
      const baseTop = parseFloat(computedStyle.top) || 0;
      
      // For elements positioned by percentage, convert to pixels
      let actualBaseTop = baseTop;
      if (computedStyle.top.includes('%')) {
        actualBaseTop = viewportHeight * (parseFloat(computedStyle.top) / 100);
      }
      
      // Calculate boundaries
      const minX = -baseLeft;
      const maxX = viewportWidth - baseLeft - elementWidth;
      const minY = -actualBaseTop;
      const maxY = viewportHeight - actualBaseTop - elementHeight;
      
      // Constrain movement
      currentX = Math.max(minX, Math.min(maxX, currentX));
      currentY = Math.max(minY, Math.min(maxY, currentY));

      xOffset = currentX;
      yOffset = currentY;

      element.style.transform = `translate(${currentX}px, ${currentY}px)`;
    }
  }

  // Mouse events
  element.addEventListener('mousedown', dragStart);
  document.addEventListener('mousemove', drag);
  document.addEventListener('mouseup', dragEnd);
  
  // Touch events
  element.addEventListener('touchstart', dragStart, { passive: true });
  document.addEventListener('touchmove', drag, { passive: false });
  document.addEventListener('touchend', dragEnd);
  
  // Return cleanup function
  return () => {
    element.removeEventListener('mousedown', dragStart);
    element.removeEventListener('touchstart', dragStart);
  };
}


// Initialize draggable panels when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Stats panel is now initialized by StatsWindow.js
});